/**
 * WashPros - Language Selector Component
 * Allows users to switch between supported languages with real-time updates
 * Syncs both i18next and LanguageContext systems, and persists the
 * choice to the backend `users.preferred_language` field so the
 * messaging-translation pipeline picks it up server-side.
 */
import React, { useState, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supportedLanguages, changeLanguage as changeI18nLanguage, getCurrentLanguage, onLanguageChange } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient, useAuth } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LanguageSelector = ({ variant = "default", className = "" }) => {
  const { i18n } = useTranslation();
  const { setLanguage: setContextLanguage } = useLanguage();
  const auth = useAuth();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes from other components
  useEffect(() => {
    const unsubscribe = onLanguageChange((langCode) => {
      setCurrentLang(langCode);
    });
    return unsubscribe;
  }, []);

  // Also update when i18n language changes
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const currentLanguage = supportedLanguages.find(lang => lang.code === currentLang) || supportedLanguages[0];

  const handleLanguageChange = (langCode, comingSoon) => {
    // Phase 7g.15.55 (2026-06-02) — block selection of comingSoon
    // locales (DE/FR/PT/ZH/JA/KO) until their translation files
    // reach full coverage.  Quietly no-op so we don't ship a half-
    // translated UI.
    if (comingSoon) return;
    // Update i18next
    changeI18nLanguage(langCode);
    // Update LanguageContext (for components using useLanguage)
    setContextLanguage(langCode);
    // Update local state
    setCurrentLang(langCode);
    // Best-effort: persist to backend so server-rendered surfaces
    // (chat translation, email digests, etc.) read the choice.
    // Authenticated only — never call this without a session token.
    try {
      if (auth?.user) {
        apiClient
          .put("/customers/me", { preferred_language: langCode })
          .catch(() => { /* silent — UI already updated */ });
      }
    } catch {
      /* useAuth not mounted */
    }
  };

  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`text-slate-400 hover:text-white min-w-[44px] min-h-[44px] touch-manipulation ${className}`}
            data-testid="language-selector-compact"
          >
            <span className="mr-1">{currentLanguage.flag}</span>
            {currentLanguage.abbr || currentLanguage.code.toUpperCase()}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onSelect={(e) => {
                // Capacitor Android WebView: onClick can be swallowed when the
                // Radix menu tears down on pointerdown. onSelect fires reliably
                // for touch/keyboard/mouse (same fix as PortalSwitcher 7g.15.99d).
                e.preventDefault();
                handleLanguageChange(lang.code, lang.comingSoon);
              }}
              data-testid={`lang-option-${lang.code}`}
              aria-disabled={lang.comingSoon || undefined}
              className={`flex items-center gap-2 ${
                lang.comingSoon
                  ? "text-slate-500 opacity-60 cursor-not-allowed"
                  : "cursor-pointer"
              } ${
                !lang.comingSoon && currentLang === lang.code ? "text-cyan-400 bg-cyan-500/10" : !lang.comingSoon ? "text-slate-300" : ""
              }`}
            >
              <span>{lang.flag}</span>
              <span className="flex-1">{lang.name}</span>
              {lang.comingSoon && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-2">
                  Coming&nbsp;soon
                </span>
              )}
              {!lang.comingSoon && currentLang === lang.code && <Check className="w-4 h-4 ml-auto" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full variant with flag and name.
  // Phase 7g.15.72 Admin-mobile Item #2 — on phone vertical (<sm),
  // collapse to a compact abbreviation (e.g., ENG / ESP) so the
  // language selector fits in the admin top header alongside the
  // notification bell, portal switcher, sound toggle, theme toggle,
  // and avatar.  Full name still shows from sm: up.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`bg-slate-800/50 border-white/10 hover:bg-slate-700 text-white px-2 sm:px-3 min-w-[44px] min-h-[44px] touch-manipulation ${className}`}
          data-testid="language-selector"
          aria-label={`Change language — current: ${currentLanguage.name}`}
        >
          <Globe className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
          <span
            className="sm:hidden ml-1 text-xs font-semibold tracking-wide"
            data-testid="language-selector-abbr"
          >
            {currentLanguage.abbr || currentLanguage.code.toUpperCase()}
          </span>
          <ChevronDown className="w-4 h-4 ml-1 sm:ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 min-w-[200px]">
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={(e) => {
              e.preventDefault();
              handleLanguageChange(lang.code, lang.comingSoon);
            }}
            data-testid={`lang-option-${lang.code}`}
            aria-disabled={lang.comingSoon || undefined}
            className={`flex items-center gap-3 py-2 ${
              lang.comingSoon
                ? "text-slate-500 opacity-60 cursor-not-allowed"
                : currentLang === lang.code
                  ? "text-cyan-400 bg-cyan-500/10 cursor-pointer"
                  : "text-slate-300 hover:bg-slate-700 cursor-pointer"
            }`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {lang.comingSoon && (
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                Coming&nbsp;soon
              </span>
            )}
            {!lang.comingSoon && currentLang === lang.code && <Check className="w-4 h-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
